import { createRng } from '@battle/sim';
import * as THREE from 'three/webgpu';

/**
 * Procedural arena floor for the look test: radial flagstones, moss creeping in from the edge,
 * and a ring of carved elemental glyphs that glow faintly (emissive map). Replaced by the
 * Blender-built Sunken Grove in M4.
 */
export function createArenaFloorTextures(size = 2048) {
  const rng = createRng(20260928);
  const color = document.createElement('canvas');
  const glow = document.createElement('canvas');
  color.width = color.height = glow.width = glow.height = size;
  const c = color.getContext('2d') as CanvasRenderingContext2D;
  const g = glow.getContext('2d') as CanvasRenderingContext2D;
  const cx = size / 2;
  const R = size / 2;

  c.fillStyle = '#857660'; // mortar: close to the stone value so mip-blurred gaps stay subtle
  c.fillRect(0, 0, size, size);
  g.fillStyle = '#000';
  g.fillRect(0, 0, size, size);

  // Concentric rings of flagstones with mortar gaps.
  const rings = [0, 0.12, 0.24, 0.36, 0.47, 0.58, 0.72, 0.84, 0.95, 1.0];
  for (let i = 0; i < rings.length - 1; i++) {
    const r0 = (rings[i] as number) * R;
    const r1 = (rings[i + 1] as number) * R;
    const count = Math.max(6, Math.round(((r0 + r1) * Math.PI) / (R * 0.16)));
    const offset = rng.next() * Math.PI * 2;
    for (let s = 0; s < count; s++) {
      const a0 = offset + (s / count) * Math.PI * 2;
      const a1 = offset + ((s + 1) / count) * Math.PI * 2;
      const shade = 150 + rng.int(-22, 18);
      const warm = rng.int(6, 22);
      c.fillStyle = `rgb(${shade + warm}, ${shade + 4}, ${shade - 18})`;
      c.beginPath();
      const gap = Math.max(1, size / 1024);
      c.arc(cx, cx, Math.max(r1 - gap, 0), a0 + gap / Math.max(r1, 1), a1 - gap / Math.max(r1, 1));
      c.arc(
        cx,
        cx,
        r0 + gap,
        a1 - gap / Math.max(r0 + gap, 1),
        a0 + gap / Math.max(r0 + gap, 1),
        true,
      );
      c.closePath();
      c.fill();
      // Subtle wear speckle.
      for (let k = 0; k < 18; k++) {
        const a = a0 + rng.next() * (a1 - a0);
        const r = r0 + rng.next() * (r1 - r0);
        c.fillStyle = `rgba(40, 32, 24, ${0.06 + rng.next() * 0.1})`;
        c.beginPath();
        c.arc(cx + Math.cos(a) * r, cx + Math.sin(a) * r, 2 + rng.next() * 10, 0, Math.PI * 2);
        c.fill();
      }
    }
  }

  // Glyph ring: carved grooves (dark in color map) that glow gold in the emissive map.
  const glyphR = 0.66 * R;
  const glyphs = 24;
  for (let i = 0; i < glyphs; i++) {
    const a = (i / glyphs) * Math.PI * 2;
    const x = cx + Math.cos(a) * glyphR;
    const y = cx + Math.sin(a) * glyphR;
    const kind = i % 4;
    for (const [ctx, stroke, width] of [
      [c, 'rgba(30, 22, 12, 0.9)', 14],
      [g, 'rgba(255, 196, 96, 1)', 7],
    ] as const) {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(a + Math.PI / 2);
      ctx.strokeStyle = stroke;
      ctx.lineWidth = width;
      ctx.lineCap = 'round';
      ctx.beginPath();
      if (kind === 0) {
        ctx.arc(0, 0, 26, 0, Math.PI * 2);
        ctx.moveTo(0, -26);
        ctx.lineTo(0, 26);
      } else if (kind === 1) {
        ctx.moveTo(-24, 20);
        ctx.lineTo(0, -26);
        ctx.lineTo(24, 20);
        ctx.closePath();
      } else if (kind === 2) {
        ctx.moveTo(-24, -12);
        ctx.quadraticCurveTo(0, 26, 24, -12);
        ctx.moveTo(-14, -26);
        ctx.lineTo(14, -26);
      } else {
        ctx.moveTo(-22, 0);
        ctx.lineTo(22, 0);
        ctx.moveTo(0, -24);
        ctx.lineTo(-12, 22);
        ctx.moveTo(0, -24);
        ctx.lineTo(12, 22);
      }
      ctx.stroke();
      ctx.restore();
    }
  }
  for (const r of [glyphR - 58, glyphR + 58]) {
    c.strokeStyle = 'rgba(30, 22, 12, 0.8)';
    c.lineWidth = 10;
    c.beginPath();
    c.arc(cx, cx, r, 0, Math.PI * 2);
    c.stroke();
    g.strokeStyle = 'rgba(255, 190, 90, 0.55)';
    g.lineWidth = 4;
    g.beginPath();
    g.arc(cx, cx, r, 0, Math.PI * 2);
    g.stroke();
  }

  // Moss creeping in from the rim.
  for (let i = 0; i < 900; i++) {
    const a = rng.next() * Math.PI * 2;
    const r = R * (0.78 + Math.sqrt(rng.next()) * 0.24);
    const x = cx + Math.cos(a) * r;
    const y = cx + Math.sin(a) * r;
    const rad = 6 + rng.next() * 34;
    const grad = c.createRadialGradient(x, y, 0, x, y, rad);
    const green = 90 + rng.int(0, 50);
    grad.addColorStop(0, `rgba(${60 + rng.int(0, 30)}, ${green}, 40, 0.55)`);
    grad.addColorStop(1, 'rgba(60, 90, 40, 0)');
    c.fillStyle = grad;
    c.beginPath();
    c.arc(x, y, rad, 0, Math.PI * 2);
    c.fill();
  }

  const map = new THREE.CanvasTexture(color);
  map.colorSpace = THREE.SRGBColorSpace;
  map.anisotropy = 8;
  const emissiveMap = new THREE.CanvasTexture(glow);
  emissiveMap.colorSpace = THREE.SRGBColorSpace;
  return { map, emissiveMap };
}
